import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import * as reduceApi from 'api/sanity/reduce';
import { AppThunk, RootState } from 'store';
import { uniqBy } from 'lodash-es';
import { showToast } from 'modules/app/toasterSlice';

const toItem = ({ _id, description, reduceCategory, title, preamble, slug }: SanityReduceItem): ReduceItem => ({
  _id,
  slug: slug.current,
  title: title.en ?? '',
  preamble: preamble?.en,
  description: description.en ?? [],
  reduceCategory: {
    _id: reduceCategory._id,
    icon: { url: reduceCategory.icon?.asset.url ?? '', alt: reduceCategory.icon?.alt ?? '' },
    title: reduceCategory.title.en ?? '',
  },
});

const toCollection = ({
  introTitle,
  introSubtitle,
  introBody,
  introImage,
  pinkBoxTitle,
  pinkBoxBody,
  footerTitle,
  footerBody,
  items,
}: SanityReduceCollection): ReduceCollection => ({
  introTitle: introTitle?.en ?? '',
  introSubtitle: introSubtitle?.en ?? '',
  introBody: introBody?.en ?? [],
  introImage: { url: introImage?.asset.url ?? '', alt: introImage?.alt ?? '' },
  pinkBoxTitle: pinkBoxTitle?.en,
  pinkBoxBody: pinkBoxBody?.en,
  footerTitle: footerTitle?.en,
  footerBody: footerBody?.en,
  items: items?.map(toItem) ?? [],
});

interface State {
  loading: boolean;
  dataset: string;
  collection: ReduceCollection;
}

const initialState: State = {
  dataset: '',
  loading: false,
  collection: {
    introTitle: '',
    introBody: [],
    introSubtitle: '',
    introImage: { url: '', alt: '' },
    items: [],
  },
};

function getUniqueItems<T>(items: Array<T>): Array<T> {
  return uniqBy(items, '_id');
}

const slice = createSlice({
  name: 'reduce',
  initialState,
  reducers: {
    setCollection: (state: State, action: PayloadAction<ReduceCollection>): State => {
      state.collection = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
    setDataset: (state: State, action: PayloadAction<string>): State => {
      state.dataset = action.payload;
      return state;
    },
  },
});
const { setLoading, setCollection, setDataset } = slice.actions;
export default slice.reducer;

export const loadCollection = (page: string): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setLoading(true));
  try {
    const data = await reduceApi.getReduceCollection(page);
    const collection = toCollection(data);

    dispatch(setCollection(collection));
    dispatch(setLoading(false));
    dispatch(setDataset(page));
  } catch {
    dispatch(setLoading(false));
  }
};

export const reduceCategories = createSelector(
  (state: RootState) => state.reduce.collection.items,
  items => getUniqueItems(items.map(item => item.reduceCategory)),
);

export const itemsByCategory = createSelector(
  (state: RootState) => state.reduce.collection.items,
  reduceCategories,
  (items, categories) =>
    categories.map(category => ({
      category,
      items: getUniqueItems(items.filter(item => item.reduceCategory._id === category._id)),
    })),
);

export const downloadChecklist = (): AppThunk => async (dispatch): Promise<void> => {
  try {
    const blob = await reduceApi.downloadChecklist();
    saveAs(blob, 'checklist.pdf');
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};
